import type { Dispatch } from 'redux';

import {
    createSelectParticipantFailedEvent,
    sendAnalytics
} from '../analytics';
import { _handleParticipantError } from '../base/conference/functions';
import { MEDIA_TYPE } from '../base/media/constants';
import { getParticipants } from '../base/participants/functions';
import { reportError } from '../base/util';
import { shouldDisplayTileView } from '../video-layout';

import {
    SELECT_LARGE_VIDEO_PARTICIPANT,
    SET_LARGE_VIDEO_FULLSCREEN,
    SET_LARGE_VIDEO_LAST_DOMINANT_SPEAKER,
    SET_NATIVE_FULLSCREEN,
    TOGGLE_SIDEBAR_MENU,
    UPDATE_KNOWN_LARGE_VIDEO_RESOLUTION
} from './actionTypes';

/**
 * Signals conference to select a participant.
 *
 * @returns {Function}
 */
export function selectParticipant() {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const state = getState();
        const { conference } = state['features/base/conference'];

        if (conference) {
            const ids = shouldDisplayTileView(state)
                ? getParticipants(state).map(participant => participant.id)
                : [ state['features/large-video'].participantId ];

            try {
                conference.selectParticipants(ids);
            } catch (err) {
                _handleParticipantError(err);

                sendAnalytics(createSelectParticipantFailedEvent(err));

                reportError(
                    err, `Failed to select participants ${ids.toString()}`);
            }
        }
    };
}

/**
 * Action to select the participant to be displayed in LargeVideo based on the
 * participant id provided. If a participant id is not provided, the LargeVideo
 * participant will be selected based on a variety of factors: If there is a
 * dominant or pinned speaker, or if there are remote tracks, etc.
 *
 * @param {string} participant - The participant id of the user that needs to be
 * displayed on the large video.
 * @param {Object} options - Selection dispatch options.
 * @param {string} options.selectionSource - Selection source identifier.
 * @param {boolean} options.forceSelection - Whether to bypass anti-flicker coalescing.
 * @returns {Function}
 */
export function selectParticipantInLargeVideo(participant: ?string, options: Object = {}) {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const state = getState();

        const participantId = participant ?? _electParticipantInLargeVideo(state);
        const largeVideo = state['features/large-video'];

        const participants = state['features/base/participants'];
        const isModeratorPinned = participants.find(p => p.moderatorPin);
        const pinnedParticipant = participants.find(p => p.pinned && !p.moderatorPin);
        const dominantSpeaker = participants.find(p => p.dominantSpeaker && !p.local);


        let newParticipantId;

        if (pinnedParticipant) {
            newParticipantId = pinnedParticipant.id;
        } else if (isModeratorPinned) {
            newParticipantId = isModeratorPinned.id;
        } else if (dominantSpeaker) {
            newParticipantId = dominantSpeaker.id;
        } else {
            newParticipantId = participantId;
        }

        if (newParticipantId && newParticipantId !== largeVideo.participantId) {
            const currentParticipantExists = largeVideo.participantId
                && participants.find(p => p?.id === largeVideo.participantId);
            const forceSelection = Boolean(options.forceSelection) || !currentParticipantExists;
            const selectionSource = options.selectionSource
                || (participant ? 'explicit' : 'auto');

            dispatch({
                type: SELECT_LARGE_VIDEO_PARTICIPANT,
                participantId: newParticipantId,
                selectionSource,
                forceSelection
            });
        }

    };
}

/**
 * Remembers the id of the last remote participant who was the dominant speaker.
 * This keeps the large video stable on the person who last spoke once the current
 * dominant speaker stops talking/mutes, instead of jumping to a random participant.
 *
 * @param {?string} participantId - The id of the last dominant speaker (remote), or
 * {@code null}/{@code undefined} to clear it.
 * @returns {{
 *     type: SET_LARGE_VIDEO_LAST_DOMINANT_SPEAKER,
 *     participantId: ?string
 * }}
 */
export function setLargeVideoLastDominantSpeaker(participantId: ?string) {
    return {
        type: SET_LARGE_VIDEO_LAST_DOMINANT_SPEAKER,
        participantId
    };
}

/**
 * Updates the currently seen resolution of the video displayed on large video.
 *
 * @param {number} resolution - The current resolution (height) of the video.
 * @returns {{
 *     type: UPDATE_KNOWN_LARGE_VIDEO_RESOLUTION,
 *     resolution: number
 * }}
 */
export function updateKnownLargeVideoResolution(resolution: number) {
    return {
        type: UPDATE_KNOWN_LARGE_VIDEO_RESOLUTION,
        resolution
    };
}

/**
 * Toggles the sidebar menu.
 *
 * @param {boolean} isSidebarMenuOpen - Whether the sidebar is open.
 * @returns {{
 *     type: TOGGLE_SIDEBAR_MENU,
 *     isSidebarMenuOpen: boolean
 * }}
 */
export function toggleSidebarMenu(isSidebarMenuOpen: boolean) {
    return {
        type: TOGGLE_SIDEBAR_MENU,
        isSidebarMenuOpen
    };
}

/**
 * Toggles the native fullscreen mode which hides NavigationBar,
 * Filmstrip, SideMenuBar, Toolbox, and tooltips.
 *
 * @param {boolean} isNativeFullScreen - Whether native fullscreen is active.
 * @returns {{
 *     type: SET_NATIVE_FULLSCREEN,
 *     isNativeFullScreen: boolean
 * }}
 */
export function setNativeFullScreen(isNativeFullScreen: boolean) {
    return {
        type: SET_NATIVE_FULLSCREEN,
        isNativeFullScreen
    };
}

/**
 * Returns the most recent existing remote video track.
 *
 * @param {Track[]} tracks - All current tracks.
 * @private
 * @returns {(Track|undefined)}
 */
function _electLastVisibleRemoteVideo(tracks) {
    // First we try to get most recent remote video track.
    for (let i = tracks.length - 1; i >= 0; --i) {
        const track = tracks[i];

        if (!track.local && track.mediaType === MEDIA_TYPE.VIDEO) {
            return track;
        }
    }
}

/**
 * Returns the identifier of the participant who is to be on the stage and
 * should be displayed in {@code LargeVideo}.
 *
 * @param {Object} state - The Redux state from which the participant to be
 * displayed in {@code LargeVideo} is to be elected.
 * @private
 * @returns {(string|undefined)}
 */
function _electParticipantInLargeVideo(state) {
    // 1. If a participant is pinned, they will be shown in the LargeVideo (
    //    regardless of whether they are local or remote).
    const participants = state['features/base/participants'];
    const isModeratorPinned = participants.find(p => p.moderatorPin);
    let participant = participants && participants.length && (participants?.find(p => p.pinned) || isModeratorPinned);
    let id = participant && participant.id;
    const pinnedParticipant = participants.find(p => p.pinned);


    if (!id) {
        if (pinnedParticipant) {
            return pinnedParticipant.id;
        } else if (isModeratorPinned) {
            return isModeratorPinned.id;
        }

        // 2. No participant is pinned so get the dominant speaker. But the
        //    local participant won't be displayed in LargeVideo even if she is
        //    the dominant speaker.
        participant = participants.find(p => p.dominantSpeaker && !p.local);
        id = participant && participant.id;

        if (!id) {
            // 3. There is no current dominant speaker. Prefer the last remote participant
            //    who was the dominant speaker (if they are still in the conference and not a
            //    bot), so the large video stays stable on the person who last spoke instead
            //    of immediately jumping to a random participant or the local video.
            const lastDominantSpeakerId = state['features/large-video']?._lastDominantSpeakerId;
            const lastDominantSpeaker = lastDominantSpeakerId
                && participants.find(p => p.id === lastDominantSpeakerId && !p.local && !p.botType);

            if (lastDominantSpeaker) {
                return lastDominantSpeaker.id;
            }

            // 4. There is no dominant speaker so select the remote participant
            //    who last had visible video.
            const tracks = state['features/base/tracks'];
            const videoTrack = _electLastVisibleRemoteVideo(tracks);

            id = videoTrack && videoTrack.participantId;

            if (!id) {
                // 4. It's possible there is no participant with visible video.
                //    This can happen for a number of reasons:
                //    - there is only one participant (i.e. the local user),
                //    - other participants joined with video muted.
                //    As a last resort, pick the last participant who joined the
                //    conference (regardless of whether they are local or
                //    remote).
                //
                // HOWEVER: We don't want to show poltergeist or other bot type participants on stage
                // automatically, because it's misleading (users may think they are already
                // joined and maybe speaking).
                for (let i = participants?.length; i > 0 && !participant; i--) {
                    const p = participants[i - 1];

                    !p.botType && (participant = p);
                }

                id = participant && participant.id;
            }
        }
    }

    return id;
}

/**
 * Sets the large video fullscreen state.
 *
 * @param {boolean} largevideofullscreen - Whether large video is fullscreen.
 * @returns {{
 *     type: SET_LARGE_VIDEO_FULLSCREEN,
 *     largevideofullscreen: boolean
 * }}
 */
export function setLargeVideoFullscreen(largevideofullscreen: boolean) {
    return {
        type: SET_LARGE_VIDEO_FULLSCREEN,
        largevideofullscreen
    };
}
